import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { ShoppingBag, Phone, ClipboardList, Store, MapPin } from "lucide-react";

export default function MarketplaceDetail() {
  const { listingId } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(false);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    api.get(`/marketplace/${listingId}`)
      .then((r) => setItem(r.data))
      .catch(() => setError(true));
  }, [listingId]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="listing-not-found">
        <h1 className="text-2xl font-black text-forest mb-2">Listing not found</h1>
        <Link to="/resources" className="text-terracotta-dark font-bold hover:underline">Back to the marketplace</Link>
      </div>
    );
  }
  if (!item) return <div className="text-center py-20 text-forest/50">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="marketplace-detail-page">
      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <div className="rounded-3xl overflow-hidden bg-sage-light h-72 sm:h-96 flex items-center justify-center shadow-sm" data-testid="listing-main-photo">
            {item.photos?.length > 0 ? (
              <img src={fileUrl(item.photos[activePhoto] || item.photos[0])} alt={item.product_name} className="w-full h-full object-contain" />
            ) : (
              <img src="/logo.png" alt="Compass Kids logo" className="max-h-40 max-w-[60%] object-contain opacity-80" data-testid="listing-main-photo-fallback" />
            )}
          </div>
          {(item.photos || []).length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3" data-testid="listing-thumbnails">
              {item.photos.map((p, i) => (
                <button key={p} onClick={() => setActivePhoto(i)}
                  className={`rounded-xl overflow-hidden border-2 transition-colors ${i === activePhoto ? "border-terracotta" : "border-transparent"}`}
                  data-testid={`thumbnail-${i}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-16 object-contain bg-sage-light" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm h-fit">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2.5 py-1 rounded-full" data-testid="listing-category">{item.category}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black text-forest" data-testid="listing-name">{item.product_name}</h1>
          {item.price && <p className="text-2xl font-black text-terracotta-dark mt-2" data-testid="listing-price">{item.price}</p>}
          <p className="text-xs text-forest/50 mt-1 flex items-center gap-1"><Store className="w-3.5 h-3.5" /> Sold by {item.seller_name}</p>
          {item.seller_location && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.seller_location)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-sage-dark hover:text-terracotta-dark hover:underline transition-colors"
              data-testid="listing-location-link"
              aria-label={`Open ${item.seller_location} in Google Maps`}
            >
              <MapPin className="w-4 h-4" /> {item.seller_location}
            </a>
          )}

          {item.description && <p className="text-forest/75 leading-relaxed mt-5 whitespace-pre-line" data-testid="listing-description">{item.description}</p>}

          <div className="mt-6 pt-5 border-t border-sage/15 space-y-3">
            {item.contact && (
              <div className="flex items-start gap-3" data-testid="listing-contact">
                <span className="w-9 h-9 rounded-xl bg-sage-light flex items-center justify-center shrink-0">
                  <Phone className="w-4 h-4 text-sage-dark" />
                </span>
                <div>
                  <p className="text-xs font-bold text-forest/50 uppercase tracking-wide">Contact the seller</p>
                  <p className="text-sm font-semibold text-forest">{item.contact}</p>
                </div>
              </div>
            )}
            {item.order_details && (
              <div className="flex items-start gap-3" data-testid="listing-order">
                <span className="w-9 h-9 rounded-xl bg-terracotta-light flex items-center justify-center shrink-0">
                  <ClipboardList className="w-4 h-4 text-terracotta-dark" />
                </span>
                <div>
                  <p className="text-xs font-bold text-forest/50 uppercase tracking-wide">How to order</p>
                  <p className="text-sm text-forest/80">{item.order_details}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
